<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import SettingsPageLayout from 'dashboard/components-next/captain/pageComponents/assistant/settings/SettingsPageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();

const carregando = ref(true);
const erro = ref(null);
const dados = ref(null);
const mostrarBruto = ref(false);

const assistantId = computed(() => route.params.assistantId);

const secoes = computed(() => {
  if (!dados.value) return [];
  return [
    {
      chave: 'diretrizes',
      titulo: t('CAPTAIN.ASSISTANTS.PROMPT.GUIDELINES.TITLE'),
      ajuda: t('CAPTAIN.ASSISTANTS.PROMPT.GUIDELINES.HELP'),
      itens: dados.value.diretrizes || [],
    },
    {
      chave: 'protecoes',
      titulo: t('CAPTAIN.ASSISTANTS.PROMPT.GUARDRAILS.TITLE'),
      ajuda: t('CAPTAIN.ASSISTANTS.PROMPT.GUARDRAILS.HELP'),
      itens: dados.value.protecoes || [],
    },
    {
      chave: 'ferramentas',
      titulo: t('CAPTAIN.ASSISTANTS.PROMPT.TOOLS.TITLE'),
      ajuda: t('CAPTAIN.ASSISTANTS.PROMPT.TOOLS.HELP'),
      itens: dados.value.ferramentas || [],
    },
  ];
});

const carregar = async () => {
  carregando.value = true;
  erro.value = null;
  try {
    const { data } = await CaptainAssistantAPI.getPrompt({
      assistantId: assistantId.value,
    });
    dados.value = data;
  } catch (e) {
    erro.value = t('CAPTAIN.ASSISTANTS.PROMPT.ERROR');
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);
</script>

<template>
  <SettingsPageLayout
    :heading="t('CAPTAIN.ASSISTANTS.PROMPT.TITLE')"
    :description="t('CAPTAIN.ASSISTANTS.PROMPT.DESCRIPTION')"
  >
    <div v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('CAPTAIN.ASSISTANTS.PROMPT.LOADING') }}
    </div>

    <div v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</div>

    <div v-else class="flex flex-col w-full gap-6">
      <section class="flex flex-col gap-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.PROMPT.IDENTITY.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.PROMPT.IDENTITY.HELP') }}
        </p>
        <div class="p-4 rounded-lg bg-n-alpha-black2">
          <p class="text-sm text-n-slate-12">
            <strong>{{ dados.identidade.nome }}</strong>
            <span v-if="dados.identidade.produto" class="text-n-slate-11">
              · {{ dados.identidade.produto }}
            </span>
          </p>
          <p class="mt-2 text-sm whitespace-pre-line text-n-slate-11">
            {{
              dados.identidade.descricao ||
              t('CAPTAIN.ASSISTANTS.PROMPT.IDENTITY.EMPTY')
            }}
          </p>
        </div>
      </section>

      <section
        v-for="secao in secoes"
        :key="secao.chave"
        class="flex flex-col gap-2"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ secao.titulo }}
          <span class="font-normal text-n-slate-11"
            >({{ secao.itens.length }})</span
          >
        </h3>
        <p class="text-sm text-n-slate-11">{{ secao.ajuda }}</p>
        <ol
          v-if="secao.itens.length"
          class="flex flex-col gap-2 pl-5 list-decimal"
        >
          <li
            v-for="(item, indice) in secao.itens"
            :key="`${secao.chave}-${indice}`"
            class="text-sm text-n-slate-12"
          >
            {{ item }}
          </li>
        </ol>
        <p v-else class="text-sm italic text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.PROMPT.EMPTY') }}
        </p>
      </section>

      <section v-if="dados.cenarios.length" class="flex flex-col gap-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.PROMPT.SCENARIOS.TITLE') }}
          <span class="font-normal text-n-slate-11"
            >({{ dados.cenarios.length }})</span
          >
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.PROMPT.SCENARIOS.HELP') }}
        </p>
        <div
          v-for="cenario in dados.cenarios"
          :key="cenario.titulo"
          class="p-4 rounded-lg bg-n-alpha-black2"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ cenario.titulo }}
          </p>
          <p class="mt-1 text-sm text-n-slate-11">{{ cenario.quando }}</p>
          <p
            v-if="cenario.ferramentas.length"
            class="mt-2 text-xs text-n-slate-11"
          >
            {{ t('CAPTAIN.ASSISTANTS.PROMPT.SCENARIOS.TOOLS') }}:
            {{ cenario.ferramentas.join(' · ') }}
          </p>
        </div>
      </section>

      <section class="flex flex-col gap-2 pt-2 border-t border-n-weak">
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('CAPTAIN.ASSISTANTS.PROMPT.RAW.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{
                t('CAPTAIN.ASSISTANTS.PROMPT.RAW.HELP', {
                  size: dados.tamanho_em_caracteres,
                })
              }}
            </p>
          </div>
          <Button
            variant="faded"
            size="sm"
            :label="
              mostrarBruto
                ? t('CAPTAIN.ASSISTANTS.PROMPT.RAW.HIDE')
                : t('CAPTAIN.ASSISTANTS.PROMPT.RAW.SHOW')
            "
            @click="mostrarBruto = !mostrarBruto"
          />
        </div>
        <pre
          v-if="mostrarBruto"
          class="p-4 overflow-x-auto text-xs whitespace-pre-wrap rounded-lg bg-n-alpha-black2 text-n-slate-11"
          >{{ dados.prompt_bruto }}</pre
        >
      </section>
    </div>
  </SettingsPageLayout>
</template>
